"use client";

import React, { useState } from "react";

import { Eye, EyeOff } from "lucide-react";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/ui/lib/utils";

export type InputFieldType = "text" | "email" | "password" | "number" | "tel" | "url" | "search";

interface InputFieldProps<T extends FieldValues> {
  readonly control: Control<T>;
  readonly name: Path<T>;
  readonly placeholder?: string;
  readonly icon?: React.ReactNode;
  readonly className?: string;
  readonly triggerClass?: string;
  readonly disabled?: boolean;
  readonly description?: string;
  readonly type?: InputFieldType;
  readonly autocomplete?: string;
  readonly showPasswordToggle?: boolean;
  readonly maxLength?: number;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  onKeyup?: (val?: string) => void;
  readonly handleFieldIconCallback?: () => void;
  readonly inputClassName?: string;
  readonly testId?: string;
}

export function InputField<T extends FieldValues>({
  control,
  name,
  placeholder = "",
  icon,
  className,
  triggerClass,
  description,
  disabled = false,
  type = "text",
  autocomplete = "off",
  showPasswordToggle = false,
  maxLength,
  onFocus,
  onClick,
  inputRef,
  onKeyup,
  handleFieldIconCallback,
  inputClassName = "",
  testId,
}: Readonly<InputFieldProps<T>>) {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = showPasswordToggle;

  let inputType: InputFieldType;

  if (isPasswordField) {
    inputType = showPassword ? "text" : "password";
  } else {
    inputType = type;
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} data-testid={testId ?? `input-field-${name}`}>
          <FormLabel hidden>{placeholder}</FormLabel>
          <FormControl>
            <div className="relative flex w-full items-center">
              <Input
                {...field}
                ref={inputRef}
                type={inputType}
                maxLength={maxLength}
                autoComplete={autocomplete}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={onFocus}
                onClick={onClick}
                onKeyUp={(e) => onKeyup?.((e.target as HTMLInputElement).value)}
                className={cn(
                  "border-brand-gray hover:border-brand-blue flex-1 rounded-md border px-3 text-sm font-normal",
                  "placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0",
                  triggerClass,
                  disabled && "pointer-events-none cursor-not-allowed opacity-50",
                  field.value
                    ? "text-brand-blue"
                    : "text-muted-foreground group-hover:text-brand-gray",
                  inputClassName,
                )}
                data-testid={`${testId ? `${testId}-input` : `${String(name).toLowerCase()}`}-input`}
              />

              {isPasswordField ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-brand-gray hover:text-brand-blue absolute top-1/2 right-3 z-10 -translate-y-1/2"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 cursor-pointer" />
                  ) : (
                    <Eye className="h-4 w-4 cursor-pointer" />
                  )}
                </button>
              ) : (
                icon &&
                React.isValidElement(icon) && (
                  <button
                    className="absolute top-1/2 right-3 z-10 -translate-y-1/2 cursor-pointer"
                    onClick={handleFieldIconCallback}
                    type="button"
                  >
                    {React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
                      className: cn(
                        "h-4 w-4 text-brand-gray",
                        (icon.props as { className?: string }).className,
                      ),
                    })}
                  </button>
                )
              )}
            </div>
          </FormControl>

          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
